/**
 * « Sources de la formation » (31/10/2026) : la base documentaire constituée par LA recherche web du dossier d'enjeux,
 * sur laquelle s'appuient parcours, tests, supports et visuels (citations « [S2] »). Lecture authentifiée : formateur
 * propriétaire et administrateur de l'organisme (le lien se partage, mais ne s'ouvre qu'une fois connecté). Le
 * formateur ajoute ou retire une source et relance la recherche (soumise au verrou et au plafond) ; l'administrateur
 * peut rouvrir exceptionnellement une génération verrouillée (motif obligatoire, journalisé).
 */
import { useCallback, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Globe, Link2, LockOpen, Plus, Trash2 } from "lucide-react";
import { LIBELLES_TYPE_SOURCE, TYPES_SOURCE, type TypeSource } from "@/domaine/pedagogie/sources";
import { api, instantFr, type BaseSources, type EtatGenerationDoc, type ResultatEnjeux } from "../api";
import { Alerte, Bouton, Carte, Champ, Chargement, EtatVide, Etiquette, Modale, Selecteur, TitrePage, ZoneTexte, cx, useNotifier } from "../ui/base";
import { generationBloquee, MentionMoteur, MentionVerrou, ProgressionTache, useEtatIa, useTacheIa } from "./AssistantIa";

const ORIGINE_EXTRAIT: Record<string, string> = { citation_api: "texte cité par la recherche web", ia: "extrait relevé par l'IA", manuel: "saisi à la main" };
const VIDE = { url: "", titre: "", type: "metier" as TypeSource, editeur: "", publie_le: "", resume: "", extrait: "" };

export function SourcesFormation() {
  const { id } = useParams({ from: "/app/formations/$id/sources" });
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const ia = useEtatIa();
  const [filtre, setFiltre] = useState<TypeSource | "tous">("tous");
  const [ajout, setAjout] = useState(false);
  const [nouvelle, setNouvelle] = useState(VIDE);
  const [aRouvrir, setARouvrir] = useState<EtatGenerationDoc | null>(null);
  const [motif, setMotif] = useState("");
  const base = useQuery({ queryKey: ["sources", id], queryFn: () => api.get<BaseSources>(`/formations/${id}/sources`) });
  const rafraichir = () => Promise.all([requetes.invalidateQueries({ queryKey: ["sources", id] }), requetes.invalidateQueries({ queryKey: ["generations", id] }), requetes.invalidateQueries({ queryKey: ["formation", id] })]);
  const ajouter = useMutation({ mutationFn: () => api.post(`/formations/${id}/sources`, nouvelle), onSuccess: async () => { await rafraichir(); setAjout(false); setNouvelle(VIDE); notifier("succes", "Source ajoutée à la base."); } });
  const retirer = useMutation({ mutationFn: (sid: string) => api.suppr(`/formations/${id}/sources/${sid}`), onSuccess: () => rafraichir() });
  const deverrouiller = useMutation({
    mutationFn: () => api.post(`/formations/${id}/generations/deverrouiller`, { cle: aRouvrir!.cle, motif }),
    onSuccess: async () => { await rafraichir(); notifier("succes", "Génération rouverte (déverrouillage journalisé)."); setARouvrir(null); setMotif(""); },
  });
  const recherche = useTacheIa<ResultatEnjeux>(
    useCallback(
      (r: ResultatEnjeux) => { void rafraichir(); notifier("succes", `Base de sources reconstituée : ${r.sources.length} source(s).`); },
      [id],
    ),
  );

  if (base.isPending) return <Chargement />;
  if (base.error) return <Alerte ton="danger">{base.error.message}</Alerte>;
  const b = base.data;
  const verrouEnjeux = b.generations.find((g) => g.cle === "enjeux") ?? null;
  const visibles = b.sources.filter((x) => filtre === "tous" || x.type === filtre);
  const compte = (t: TypeSource) => b.sources.filter((x) => x.type === t).length;

  return (
    <>
      {b.droits.modifier ? (
        <Link to="/formations/$id" params={{ id }} className="mb-3 inline-flex items-center gap-1.5 text-sm text-encre-3 hover:text-encre"><ArrowLeft className="size-4" aria-hidden /> {b.formation.titre}</Link>
      ) : (
        <Link to="/coffres/$id" params={{ id }} className="mb-3 inline-flex items-center gap-1.5 text-sm text-encre-3 hover:text-encre"><ArrowLeft className="size-4" aria-hidden /> Coffre-fort du parcours</Link>
      )}
      <TitrePage
        titre="Sources de la formation"
        soustitre={`${b.formation.titre} — ${b.sources.length} source(s)${b.formation.enjeux_le ? `, recherche du ${instantFr(b.formation.enjeux_le)}` : ""}. Parcours, tests, supports et visuels les citent par leur numéro (« [S2] »).`}
        actions={
          <Bouton icone={<Link2 className="size-4" aria-hidden />} onClick={() => void navigator.clipboard?.writeText(location.href).then(() => notifier("succes", "Lien copié : il ne s'ouvre qu'après connexion, pour le formateur de la formation et l'organisme."))}>
            Copier le lien
          </Bouton>
        }
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,1.3fr)]">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrer par type">
            {(["tous", ...TYPES_SOURCE] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={filtre === t}
                onClick={() => setFiltre(t)}
                className={cx("h-8 rounded-full border px-3 text-[13px] font-medium", filtre === t ? "border-accent bg-accent text-sur-accent" : "border-trait-fort bg-carte hover:bg-papier-2")}
              >
                {t === "tous" ? `Toutes (${b.sources.length})` : `${LIBELLES_TYPE_SOURCE[t]} (${compte(t)})`}
              </button>
            ))}
          </div>

          {visibles.length === 0 ? (
            <EtatVide icone={<Globe className="size-8" aria-hidden />} titre={b.sources.length ? "Aucune source de ce type" : "La base de sources est vide"}>
              {b.sources.length ? "Choisissez un autre filtre." : "Elle se constitue avec l'analyse des enjeux (recherche web) ou la génération du parcours ; vous pouvez aussi ajouter des sources à la main."}
            </EtatVide>
          ) : (
            <ol className="space-y-3">
              {visibles.map((x) => (
                <li key={x.id}>
                  <Carte className="p-4" data-testid="source">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="chiffres rounded-sm bg-accent px-1.5 py-0.5 text-[12px] font-semibold text-sur-accent">S{x.rang}</span>
                        <Etiquette ton={x.type === "reglementaire" ? "accent" : undefined}>{LIBELLES_TYPE_SOURCE[x.type]}</Etiquette>
                        {x.origine === "manuel" && <Etiquette ton="attente">Ajoutée à la main</Etiquette>}
                      </p>
                      {b.droits.modifier && (
                        <button type="button" className="inline-flex items-center gap-1 text-[12.5px] text-encre-3 hover:text-danger" onClick={() => confirm(`Retirer la source S${x.rang} ? Les prochaines générations ne la citeront plus.`) && retirer.mutate(x.id)}>
                          <Trash2 className="size-3.5" aria-hidden /> Retirer
                        </button>
                      )}
                    </div>
                    <a href={x.url} target="_blank" rel="noreferrer noopener" className="mt-2 inline-flex items-start gap-1.5 font-medium text-accent underline-offset-4 hover:underline">
                      <span className="break-words">{x.titre}</span>
                      <ExternalLink className="mt-1 size-3.5 shrink-0" aria-hidden />
                    </a>
                    <p className="mt-0.5 text-[12.5px] text-encre-3">
                      {[x.editeur, x.publie_le && `publié : ${x.publie_le}`, `consulté le ${instantFr(x.consulte_le)}`].filter(Boolean).join(" · ")}
                    </p>
                    {x.resume && <p className="mt-2 text-sm leading-relaxed">{x.resume}</p>}
                    {x.extrait && (
                      <blockquote className="mt-2 border-l-2 border-accent/60 pl-3 text-[13px] leading-relaxed text-encre-2">
                        « {x.extrait} »<span className="mt-0.5 block text-[12px] text-encre-3">{ORIGINE_EXTRAIT[x.extrait_origine] ?? ""}</span>
                      </blockquote>
                    )}
                  </Carte>
                </li>
              ))}
            </ol>
          )}
          {b.retirees > 0 && <p className="text-[12.5px] text-encre-3">{b.retirees} source(s) retirée(s) ou remplacée(s) par une nouvelle recherche : elles restent conservées, hors des générations.</p>}
        </div>

        <div className="min-w-0 space-y-4">
          {b.droits.modifier && (
            <Carte className="space-y-3 p-4">
              <h2 className="text-base font-semibold">Enrichir la base</h2>
              <Bouton icone={<Plus className="size-4" aria-hidden />} onClick={() => setAjout(true)}>Ajouter une source</Bouton>
              <div className="space-y-2 border-t border-trait pt-3">
                <p className="text-[13px] text-encre-2">Relancer la recherche web remplace les sources issues de la recherche précédente ; vos ajouts manuels sont conservés.</p>
                {!verrouEnjeux?.verrouille && (
                  <Bouton icone={<Globe className="size-4" aria-hidden />} disabled={!ia.disponible || recherche.enCours || generationBloquee(verrouEnjeux)} onClick={() => confirm("Relancer la recherche documentaire ?") && recherche.lancer({ type: "enjeux", formation_id: id })}>
                    Relancer la recherche
                  </Bouton>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <MentionVerrou etat={verrouEnjeux} />
                  {ia.disponible && <MentionMoteur description={ia.description} />}
                </div>
                <ProgressionTache tache={recherche} libelle="Recherche documentaire" />
                {recherche.erreur && <Alerte ton="danger">{recherche.erreur}</Alerte>}
              </div>
            </Carte>
          )}

          <Carte className="p-4">
            <h2 className="text-base font-semibold">Générations IA de la formation</h2>
            <p className="mt-0.5 text-[13px] text-encre-2">Trois générations au plus par document avant enregistrement ; une fois enregistré, le document ne se régénère plus (il reste modifiable à la main).</p>
            <ul className="mt-3 divide-y divide-trait text-sm">
              {b.generations.map((g) => (
                <li key={g.cle} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="min-w-0">
                    <span className="block font-medium">{g.libelle}</span>
                    <MentionVerrou etat={g} />
                    {g.deverrouille_le && <span className="block text-[12px] text-encre-3">Rouvert le {instantFr(g.deverrouille_le)} — motif : {g.motif_deverrouillage}</span>}
                  </span>
                  {b.droits.deverrouiller && (g.verrouille || g.essais_restants <= 0) && (
                    <Bouton taille="sm" icone={<LockOpen className="size-3.5" aria-hidden />} onClick={() => setARouvrir(g)}>Déverrouiller</Bouton>
                  )}
                </li>
              ))}
            </ul>
          </Carte>
        </div>
      </div>

      <Modale ouverte={ajout} fermer={() => setAjout(false)} titre="Ajouter une source">
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); ajouter.mutate(); }}>
          <Champ libelle="Adresse (https://…)" type="url" required value={nouvelle.url} onChange={(e) => setNouvelle({ ...nouvelle, url: e.target.value })} />
          <Champ libelle="Titre" required value={nouvelle.titre} onChange={(e) => setNouvelle({ ...nouvelle, titre: e.target.value })} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Selecteur libelle="Type" value={nouvelle.type} onChange={(e) => setNouvelle({ ...nouvelle, type: e.target.value as TypeSource })}>
              {TYPES_SOURCE.map((t) => <option key={t} value={t}>{LIBELLES_TYPE_SOURCE[t]}</option>)}
            </Selecteur>
            <Champ libelle="Date de publication (si connue)" value={nouvelle.publie_le} onChange={(e) => setNouvelle({ ...nouvelle, publie_le: e.target.value })} />
          </div>
          <Champ libelle="Éditeur ou site" value={nouvelle.editeur} onChange={(e) => setNouvelle({ ...nouvelle, editeur: e.target.value })} />
          <ZoneTexte libelle="Résumé (une phrase)" rows={2} value={nouvelle.resume} onChange={(e) => setNouvelle({ ...nouvelle, resume: e.target.value })} />
          <ZoneTexte libelle="Extrait clé (recopié mot pour mot)" rows={3} aide="Les graphiques chiffrés ne reprennent que des chiffres présents dans l'extrait d'une source." value={nouvelle.extrait} onChange={(e) => setNouvelle({ ...nouvelle, extrait: e.target.value })} />
          {ajouter.error && <Alerte ton="danger">{ajouter.error.message}</Alerte>}
          <Bouton type="submit" variante="primaire" enCours={ajouter.isPending}>Ajouter</Bouton>
        </form>
      </Modale>

      <Modale ouverte={aRouvrir !== null} fermer={() => setARouvrir(null)} titre="Rouvrir la génération">
        {aRouvrir && (
          <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); deverrouiller.mutate(); }}>
            <p className="text-sm">
              <strong>{aRouvrir.libelle}</strong> — le document déjà enregistré n'est pas modifié ; le formateur pourra de nouveau lancer jusqu'à trois générations. Le déverrouillage est journalisé avec son motif.
            </p>
            <ZoneTexte libelle="Motif (obligatoire)" rows={3} required value={motif} onChange={(e) => setMotif(e.target.value)} />
            {deverrouiller.error && <Alerte ton="danger">{deverrouiller.error.message}</Alerte>}
            <Bouton type="submit" variante="primaire" enCours={deverrouiller.isPending} icone={<LockOpen className="size-4" aria-hidden />}>Déverrouiller</Bouton>
          </form>
        )}
      </Modale>
    </>
  );
}
